import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Link
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { sendLoginToken } from '../store/slices/authSlice';
import imgUrl from '../assets/images/imgUrl';
import Swal from 'sweetalert2';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  
  const isUpdatePassword = searchParams.get('updatePassword') === 'true';
  const prefillEmail = searchParams.get('email') || '';
  
  const [email, setEmail] = useState(prefillEmail);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  const { otpLoading } = useSelector((state) => state.auth);

  // Set email from URL param if provided
  useEffect(() => {
    if (prefillEmail) {
      setEmail(prefillEmail);
    }
  }, [prefillEmail]);

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    setError('');
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async () => {
    
    // Validate email
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }
    
    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    setError('');
    
    try {
      const result = await dispatch(sendLoginToken(email));
      
      if (sendLoginToken.fulfilled.match(result)) {
        setSuccess(true);
        Swal.fire({
          icon: 'success',
          title: 'OTP Sent!',
          text: 'A password reset OTP has been sent to your email address.',
          confirmButtonColor: '#3A8139'
        });
        Swal.fire({
          title: 'OTP Sent!',
          text: 'A password reset OTP has been sent to your email address.',
          icon: 'success',
          showConfirmButton: true,
          confirmButtonText: 'Continue',
          customClass: {
            popup: 'minimal-popup',
            confirmButton: 'minimal-button',
          },
          backdrop: false,
          allowOutsideClick: true,
          buttonsStyling: false,
        }).then(() => {
          // Redirect to SetUpPassword page with email and updatePassword parameters
          const updatePasswordParam = isUpdatePassword ? '&updatePassword=true' : '';
          navigate(`/setuppassword?email=${encodeURIComponent(email)}${updatePasswordParam}`);
        });
      } else if (sendLoginToken.rejected.match(result)) {
        const errorMessage = result.payload?.message || 'Failed to send OTP. Please try again.';
        setError(errorMessage);
      }
    } catch (error) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  return (
    <Box
      sx={{
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: "hidden"
      }}
      className="LoginSection"
    >
      <Paper elevation={5} sx={{ p: 4, width: 360, textAlign: 'center', borderRadius: 2 }} className='UserSubSection'>
        {/* Logo */}
        <Box sx={{ mb: 2 }}>
          <img src={imgUrl.peaceMovImg} alt="Logo" width="100" />
        </Box>

        {/* Heading */}
        <Typography variant="h6" fontWeight="400" mb={0} className='loginHeading'>
          {isUpdatePassword ? 'UPDATE PASSWORD' : 'FORGOT PASSWORD'}
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          {isUpdatePassword ? 'Enter your email to update password' : 'Enter your email to reset password'}
        </Typography>

        {/* Email Input */}
        <Typography sx={{ textAlign: "left" }}>Email Address</Typography>
        <TextField
          fullWidth
          label=""
          placeholder='Please enter email'
          type="email"
          required
          variant="outlined"
          margin="dense"
          size="small"
          sx={{ mb: 2 }}
          value={email}
          onChange={handleEmailChange}
          error={!!error}
          disabled={isSubmitting}
        />

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2, fontSize: '0.875rem' }}>
            {error}
          </Alert>
        )}

        {/* Success Alert */}
        {success && (
          <Alert severity="success" sx={{ mb: 2, borderRadius: 2, fontSize: '0.875rem' }}>
            OTP sent successfully! Please check your email.
          </Alert>
        )}

        {/* Submit Button */}
        <Button
          fullWidth
          variant="contained"
          disabled={isSubmitting || otpLoading}
          sx={{
            mt: 2,
            background: 'linear-gradient(to right, #214F7D, #3A8139)',
            color: 'white',
            borderRadius: "50px"
          }}
          onClick={handleSubmit}
        >
          {isSubmitting || otpLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
              <CircularProgress size={18} color="inherit" />
              Sending OTP...
            </Box>
          ) : (
            isUpdatePassword ? 'Send Update OTP' : 'Send Reset OTP'
          )}
        </Button>

        {/* Back to Login Link */}
        <Link 
          underline="hover" 
          sx={{ 
            display: 'block', 
            mt: 3, 
            fontSize: 14, 
            textDecoration: "underline",
            color: '#666'
          }} 
          className='cursor-pointer' 
          onClick={handleBackToLogin}
        >
          Back to Login
        </Link>

        {/* Register Link */}
        {!window.cordova ?
          <Link 
            underline="hover" 
            sx={{ 
              display: 'block', 
              mt: 2, 
              fontSize: 14, 
              textDecoration: "underline",
              color: '#666'
            }} 
            className='cursor-pointer' 
            onClick={() => navigate("/register?referencenumber=IN-0000021-PK")}
          >
            Register as Peacekeeper
          </Link> : ""
        }
      </Paper>
    </Box>
  );
};

export default ForgotPassword;
